import { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor as useTiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import CodeBlock from '@tiptap/extension-code-block';
import Blockquote from '@tiptap/extension-blockquote';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { formatDateForInput, formatDateForDisplay } from '../utils/dateUtils';
import { checkGrammar } from '../utils/grammarChecker';

const DIARY_DRAFT_KEY = 'idiawisdom_diary_draft';

export const useDiaryEditor = (submitDiary, updateDiary, user, categories = []) => {
  const [isComposing, setIsComposing] = useState(false);
  const [editingDiaryId, setEditingDiaryId] = useState(null);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeAuthor, setComposeAuthor] = useState('');
  const [composeType, setComposeType] = useState('book');
  const [composeDate, setComposeDate] = useState('');
  const [composeTopic, setComposeTopic] = useState('');
  const [composePageNumber, setComposePageNumber] = useState('');
  const [composeLink, setComposeLink] = useState('');
  const [composeCategory, setComposeCategory] = useState(categories[0] || 'General');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [liveStats, setLiveStats] = useState({ words: 0, time: 0 });
  const [localDraftAvailable, setLocalDraftAvailable] = useState(false);
  const [grammarIssues, setGrammarIssues] = useState([]);
  const [isCheckingGrammar, setIsCheckingGrammar] = useState(false);

  const lastSavedRef = useRef({ title: '', content: '', author: '', type: '', rating: '', link: '', category: '' });
  const debounceTimerRef = useRef(null);
  const handleSaveDiaryRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());

  // Stable refs to prevent stale closure bugs in Tiptap's callbacks
  const editingDiaryIdRef = useRef(editingDiaryId);
  const composeTitleRef = useRef(composeTitle);
  const composeAuthorRef = useRef(composeAuthor);
  const composeTypeRef = useRef(composeType);
  const composeDateRef = useRef(composeDate);
  const composeTopicRef = useRef(composeTopic);
  const composePageNumberRef = useRef(composePageNumber);
  const composeLinkRef = useRef(composeLink);
  const composeCategoryRef = useRef(composeCategory);
  const isComposingRef = useRef(isComposing);

  useEffect(() => {
    editingDiaryIdRef.current = editingDiaryId;
    composeTitleRef.current = composeTitle;
    composeAuthorRef.current = composeAuthor;
    composeTypeRef.current = composeType;
    composeDateRef.current = composeDate;
    composeTopicRef.current = composeTopic;
    composePageNumberRef.current = composePageNumber;
    composeLinkRef.current = composeLink;
    composeCategoryRef.current = composeCategory;
    isComposingRef.current = isComposing;
  }, [editingDiaryId, composeTitle, composeAuthor, composeType, composeDate, composeTopic, composePageNumber, composeLink, composeCategory, isComposing]);

  // Check if a local draft is available
  useEffect(() => {
    const draft = localStorage.getItem(DIARY_DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        const hasData = parsed.title?.trim() || (parsed.content && parsed.content !== '<p></p>');
        setLocalDraftAvailable(!!hasData);
      } catch (e) {
        setLocalDraftAvailable(false);
      }
    } else {
      setLocalDraftAvailable(false);
    }
  }, [isComposing]);

  // --- Tiptap Engine Initialization ---
  const editor = useTiptapEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      CodeBlock,
      Blockquote,
      HorizontalRule,
      Placeholder.configure({
        placeholder: 'Write your reflection...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[40vh] focus:outline-none prose prose-stone dark:prose-invert max-w-none',
        spellcheck: 'false',
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText() || '';
      const words = text.trim() ? text.trim().split(/\s+/).length : 0;
      setLiveStats({ words, time: Math.max(1, Math.ceil(words / 200)) });

      // Save local draft to localStorage in real-time
      if (isComposingRef.current) {
        const content = editor.getHTML();
        if (composeTitleRef.current.trim() || (content && content !== '<p></p>')) {
          localStorage.setItem(DIARY_DRAFT_KEY, JSON.stringify({
            id: editingDiaryIdRef.current,
            title: composeTitleRef.current,
            author: composeAuthorRef.current,
            type: composeTypeRef.current,
            date: composeDateRef.current,
            topic: composeTopicRef.current,
            pageNumber: composePageNumberRef.current,
            link: composeLinkRef.current,
            category: composeCategoryRef.current,
            content: content,
            timestamp: Date.now()
          }));
          setLocalDraftAvailable(true);
        }
      }

      triggerSmartSync();
    },
  });

  // Save to localStorage when metadata fields change
  useEffect(() => {
    if (isComposing && editor) {
      const content = editor.getHTML();
      if (composeTitle.trim() || (content && content !== '<p></p>')) {
        localStorage.setItem(DIARY_DRAFT_KEY, JSON.stringify({
          id: editingDiaryId,
          title: composeTitle,
          author: composeAuthor,
          type: composeType,
          date: composeDate,
          topic: composeTopic,
          pageNumber: composePageNumber,
          link: composeLink,
          category: composeCategory,
          content: content,
          timestamp: Date.now()
        }));
        setLocalDraftAvailable(true);
      }
    }
  }, [composeTitle, composeAuthor, composeType, composeDate, composeTopic, composePageNumber, composeLink, composeCategory, isComposing, editor, editingDiaryId]);

  // Keep stable ref for Firebase autosave
  useEffect(() => {
    handleSaveDiaryRef.current = handleSaveDiary;
  });

  // --- Autosave Trigger ---
  const triggerSmartSync = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (handleSaveDiaryRef.current) {
        handleSaveDiaryRef.current('draft', true);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const executeCommand = (command) => {
    if (!editor) return;
    const chain = editor.chain().focus();
    switch (command) {
      case 'bold': chain.toggleBold().run(); break;
      case 'italic': chain.toggleItalic().run(); break;
      case 'underline': chain.toggleUnderline().run(); break;
      case 'strike': chain.toggleStrike().run(); break;
      case 'h1': chain.toggleHeading({ level: 1 }).run(); break;
      case 'h2': chain.toggleHeading({ level: 2 }).run(); break;
      case 'h3': chain.toggleHeading({ level: 3 }).run(); break;
      case 'bullet': chain.toggleBulletList().run(); break;
      case 'number': chain.toggleOrderedList().run(); break;
      case 'blockquote': chain.toggleBlockquote().run(); break;
      case 'codeBlock': chain.toggleCodeBlock().run(); break;
      case 'divider': chain.setHorizontalRule().run(); break;
      case 'emDash': chain.insertContent('—').run(); break;
      case 'undo': chain.undo().run(); break;
      case 'redo': chain.redo().run(); break;
      default: break;
    }
  };

  const handleGrammarCheck = useCallback(async () => {
    if (!editor || isCheckingGrammar) return;
    const text = editor.getText();
    if (!text.trim()) return;

    setIsCheckingGrammar(true);
    setGrammarIssues([]);

    try {
      const results = await checkGrammar(text);
      setGrammarIssues(results);
    } catch (e) {
      console.error('Grammar check error:', e);
    } finally {
      setIsCheckingGrammar(false);
    }
  }, [editor, isCheckingGrammar]);

  const replaceGrammarFix = useCallback((error) => {
    if (!editor) return;
    const { doc } = editor.state;
    const fullText = doc.textBetween(0, doc.content.size, '\n', false);
    const idx = fullText.indexOf(error.word);
    if (idx === -1) return;

    editor
      .chain()
      .focus()
      .setTextSelection({ from: idx, to: idx + error.word.length })
      .deleteSelection()
      .insertContent(error.suggestion)
      .run();

    setGrammarIssues(prev => prev.filter(i => i.word !== error.word));
  }, [editor]);

  const clearGrammarIssues = useCallback(() => {
    setGrammarIssues([]);
    setIsCheckingGrammar(false);
  }, []);

  const ignoreGrammarIssue = useCallback((index) => {
    setGrammarIssues(prev => prev.filter((_, i) => i !== index));
  }, []);

  const openNewDiaryEditor = (defaultBook = null) => {
    // If a default book is provided, pre-fill from that or from last used book
    const book = defaultBook || (() => {
      try {
        const saved = localStorage.getItem('idiawisdom_last_book');
        return saved ? JSON.parse(saved) : null;
      } catch { return null; }
    })();

    setEditingDiaryId(null);
    setComposeTitle(book?.title || '');
    setComposeAuthor(book?.author || '');
    setComposeType(book?.type || 'book');
    setComposeDate(formatDateForInput(new Date()));
    setComposeTopic('');
    setComposePageNumber('');
    setComposeLink('');
    setComposeCategory(categories[0] || 'General');
    setIsComposing(true);
    setSaveStatus('idle');
    lastSavedRef.current = {
      title: '', content: '', author: '', type: 'book',
      topic: '', pageNumber: '', link: '', category: categories[0] || 'General'
    };
    if (editor) editor.commands.setContent('');
  };

  const openEditDiaryEditor = (entry) => {
    setEditingDiaryId(entry.id);
    setComposeTitle(entry.title || '');
    setComposeAuthor(entry.author || '');
    setComposeType(entry.type || 'book');
    setComposeDate(formatDateForInput(entry.date));
    setComposeTopic(entry.topic || '');
    setComposePageNumber(entry.pageNumber || '');
    setComposeLink(entry.link || '');
    setComposeCategory(entry.category || categories[0] || 'General');
    setIsComposing(true);
    if (editor) {
      editor.commands.setContent(entry.content || '');
      lastSavedRef.current = {
        title: entry.title || '', content: entry.content || '',
        author: entry.author || '', type: entry.type || 'book',
        topic: entry.topic || '', pageNumber: entry.pageNumber || '',
        link: entry.link || '', category: entry.category || categories[0] || 'General'
      };
      const text = editor.getText() || '';
      setLiveStats({
        words: text.trim() ? text.trim().split(/\s+/).length : 0,
        time: Math.max(1, Math.ceil(text.length / 200))
      });
    }
  };

  const restoreLocalDraft = () => {
    const draft = localStorage.getItem(DIARY_DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setEditingDiaryId(parsed.id || null);
        setComposeTitle(parsed.title || '');
        setComposeAuthor(parsed.author || '');
        setComposeType(parsed.type || 'book');
        setComposeDate(parsed.date || formatDateForInput(new Date()));
        setComposeTopic(parsed.topic || '');
        setComposePageNumber(parsed.pageNumber || '');
        setComposeLink(parsed.link || '');
        setComposeCategory(parsed.category || categories[0] || 'General');
        if (editor) {
          editor.commands.setContent(parsed.content || '');
          const text = editor.getText() || '';
          setLiveStats({
            words: text.trim() ? text.trim().split(/\s+/).length : 0,
            time: Math.max(1, Math.ceil(text.length / 200))
          });
        }
        setLocalDraftAvailable(false);
      } catch (e) {
        console.error("Failed to restore diary draft:", e);
      }
    }
  };

  const clearLocalDraft = () => {
    localStorage.removeItem(DIARY_DRAFT_KEY);
    setLocalDraftAvailable(false);
  };

  const closeEditor = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setIsComposing(false);
    setEditingDiaryId(null);
    clearLocalDraft();
    clearGrammarIssues();
  };

  const handleSaveDiary = async (statusToSave, isAutosave = false) => {
    if (!user || !editor) return;

    // Serialize saves so they never race: each save chains after the previous one
    const doSave = async () => {
      // For manual saves, cancel any pending autosave timer first
      if (!isAutosave && debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      const currentTitle = composeTitle.trim();
      const content = editor.getHTML();
      const textOnly = editor.getText().trim();

      if (isAutosave && !currentTitle && !textOnly) return;
      if (isAutosave &&
          lastSavedRef.current.title === currentTitle &&
          lastSavedRef.current.content === content &&
          lastSavedRef.current.author === composeAuthor &&
          lastSavedRef.current.type === composeType &&
          lastSavedRef.current.topic === composeTopic &&
          lastSavedRef.current.pageNumber === composePageNumber &&
          lastSavedRef.current.link === composeLink &&
          lastSavedRef.current.category === composeCategory) {
        return;
      }

      setSaveStatus('saving');

      const diaryData = {
        title: currentTitle || 'Untitled Reflection',
        content: content,
        author: composeAuthor.trim(),
        type: composeType,
        category: composeCategory,
        date: formatDateForDisplay(composeDate),
        topic: composeTopic.trim(),
        pageNumber: composePageNumber.trim(),
        link: composeLink.trim(),
        status: statusToSave,
        updatedAt: Date.now()
      };

      try {
        let result;
        // Use ref (not stale state) to check if a previous queued save already created this entry
        if (editingDiaryIdRef.current) {
          result = await updateDiary(editingDiaryIdRef.current, diaryData);
        } else {
          result = await submitDiary(diaryData);
        }
        if (result && result.success) {
          lastSavedRef.current = {
            title: currentTitle, content: content, author: composeAuthor,
            type: composeType, topic: composeTopic, pageNumber: composePageNumber,
            link: composeLink, category: composeCategory
          };
          // Save this book as the default for next new entry
          if (currentTitle) {
            localStorage.setItem('idiawisdom_last_book', JSON.stringify({
              title: currentTitle,
              author: composeAuthor.trim(),
              type: composeType
            }));
          }
          setSaveStatus('success');
          clearLocalDraft();
          if (!isAutosave && statusToSave === 'published') closeEditor();
          else if (!editingDiaryIdRef.current && result.id) {
            setEditingDiaryId(result.id);
            editingDiaryIdRef.current = result.id; // update ref immediately for next queued save
          }
        }
      } catch (e) {
        console.error("Failed to save diary entry:", e);
      }
      setTimeout(() => setSaveStatus('idle'), 3000);
    };

    // Chain: wait for previous save, then run this one
    const prev = saveQueueRef.current;
    saveQueueRef.current = prev.then(doSave, doSave);
    return saveQueueRef.current;
  };

  return {
    isComposing,
    editingDiaryId,
    composeTitle, setComposeTitle,
    composeAuthor, setComposeAuthor,
    composeType, setComposeType,
    composeDate, setComposeDate,
    composeTopic, setComposeTopic,
    composeLink, setComposeLink,
    composeCategory, setComposeCategory,
    saveStatus,
    editor,
    executeCommand,
    openNewDiaryEditor,
    openEditDiaryEditor,
    closeEditor,
    handleSaveDiary,
    liveStats,
    triggerSmartSync,
    localDraftAvailable,
    restoreLocalDraft,
    clearLocalDraft,
    grammarIssues,
    isCheckingGrammar,
    handleGrammarCheck,
    replaceGrammarFix,
    clearGrammarIssues,
    ignoreGrammarIssue
  };
};
